import { useEffect, useState } from 'react';
import { FileCheck2, Gauge, ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';

interface HealthSummary {
    templates: { counts: { approved: number; rejected: number; paused: number; other: number }; tracked: number };
    quality: { counts: { green: number; yellow: number; red: number; unknown: number }; tracked: number };
    account: { health: 'healthy' | 'issue' | 'no_data'; lastEvent: string | null; reason: string | null; at: string | null };
}

interface Props {
    token: string;
    onOpenDetails: () => void;
}

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Compact health pills for the Messaging Controls header: template approval
 * state, worst quality score and WABA account health, fed by Meta webhook
 * events. Clicking any pill opens the Messaging Quality tab.
 */
export default function MessagingHealthWidget({ token, onOpenDetails }: Props) {
    const [summary, setSummary] = useState<HealthSummary | null>(null);

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/saas/messaging-health/summary`, {
            headers: { Authorization: `Bearer ${token}`, 'x-client-type': 'web' }
        })
            .then(r => r.json())
            .then(json => { if (json.success) setSummary(json.data); })
            .catch(() => { /* ignore */ });
    }, [token]);

    if (!summary) return null;

    const t = summary.templates.counts;
    const templateIssues = t.rejected + t.paused;
    const templatePill = summary.templates.tracked === 0
        ? { cls: 'bg-gray-100 text-gray-500', text: 'No signals' }
        : templateIssues > 0
            ? { cls: 'bg-red-100 text-red-700', text: `${templateIssues} issue${templateIssues > 1 ? 's' : ''}` }
            : { cls: 'bg-green-100 text-green-700', text: `${t.approved} approved` };

    const q = summary.quality.counts;
    const qualityPill = summary.quality.tracked === 0
        ? { cls: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400', text: 'No signals' }
        : q.red > 0
            ? { cls: 'bg-red-100 text-red-700', dot: 'bg-red-500', text: `${q.red} red` }
            : q.yellow > 0
                ? { cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', text: `${q.yellow} yellow` }
                : { cls: 'bg-green-100 text-green-700', dot: 'bg-green-500', text: 'All green' };

    const account = summary.account.health === 'issue'
        ? { cls: 'bg-red-100 text-red-700', Icon: ShieldAlert, text: summary.account.lastEvent?.replace(/_/g, ' ').toLowerCase() || 'Issue' }
        : summary.account.health === 'healthy'
            ? { cls: 'bg-green-100 text-green-700', Icon: ShieldCheck, text: 'Healthy' }
            : { cls: 'bg-gray-100 text-gray-500', Icon: ShieldQuestion, text: 'No signals' };

    const pillBase = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer hover:opacity-80 transition-opacity';

    return (
        <div className="flex items-center gap-2 flex-wrap" title="Meta messaging health — click for details">
            <button onClick={onOpenDetails} className={`${pillBase} ${templatePill.cls}`} title="Template approval status">
                <FileCheck2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Templates:</span> {templatePill.text}
            </button>
            <button onClick={onOpenDetails} className={`${pillBase} ${qualityPill.cls}`} title="Template quality scores">
                <Gauge className="h-3.5 w-3.5" />
                <span className={`h-2 w-2 rounded-full ${qualityPill.dot}`} />
                <span className="hidden sm:inline">Quality:</span> {qualityPill.text}
            </button>
            <button
                onClick={onOpenDetails}
                className={`${pillBase} ${account.cls}`}
                title={summary.account.reason || 'WhatsApp Business account health'}
            >
                <account.Icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Account:</span> <span className="capitalize">{account.text}</span>
            </button>
        </div>
    );
}
